/**
 * 大海龟旅店演出时间线（数据驱动）。
 *  inn-arrive     进店前的一眼（可跳过，不设旗标）
 *  inn-sit-down   挤进去、落座、点苦酒、环顾（不可跳过；不设旗标）
 *  inn-argument   同桌争论 → 终止步 setFlag INN_ARGUMENT
 *  inn-omen       不祥的预感 → 定格于赵呈博的注视 → 淡出 → setFlag OMEN_END
 * 情绪递进：罗大湖 兴奋(flushed) → 暗淡(dim)；江子潮 不安；赵呈博 眉头紧缩注视罗大湖（最后一步）。
 * 身体帧：emote / say / anim 都经场景的姿态导演落到图集真有的帧（罗大湖 sittalk→dim，吴庸 sittalk→boast，酒客 laugh）。
 */
import type { TimelineData, TimelineStep } from '@/core/types/contracts';
import { CAST, LAUGHERS, SEATS, WU_STAND, feetOf } from './innCast';

const stairsTop = feetOf(12, 6);
const tableCentre = { x: SEATS.player.x - 32, y: SEATS.player.y - 8 };

export const INN_TIMELINES: TimelineData[] = [
  {
    id: 'inn-arrive',
    skippable: true,
    steps: [
      { op: 'camera', x: 15 * 32, y: 8 * 32, ms: 1400 },
      { op: 'say', dialogue: 'inn-obs-arrive' },
      { op: 'camera', follow: true, ms: 700 },
      { op: 'setMark', mark: 'inn:arrived' },
    ],
  },
  {
    id: 'inn-sit-down',
    skippable: false,
    steps: [
      { op: 'sfx', key: 'stool-scrape' },
      { op: 'move', actor: CAST.player, x: SEATS.player.x, y: SEATS.player.y, speed: 70, wait: true },
      { op: 'face', actor: CAST.player, facing: SEATS.player.facing },
      { op: 'anim', actor: CAST.player, state: 'sit' },
      { op: 'move', actor: CAST.zhao, x: SEATS.zhao.x, y: SEATS.zhao.y, speed: 60, wait: true },
      { op: 'face', actor: CAST.zhao, facing: SEATS.zhao.facing },
      { op: 'anim', actor: CAST.zhao, state: 'sit' },
      { op: 'say', dialogue: 'inn-sit-order-a' },
      { op: 'move', actor: CAST.waiter, x: SEATS.player.x + 30, y: SEATS.player.y + 10, speed: 90, wait: true },
      { op: 'face', actor: CAST.waiter, facing: 'left' },
      { op: 'sfx', key: 'cup-set' },
      { op: 'say', dialogue: 'inn-sit-order-b' },
      { op: 'move', actor: CAST.waiter, x: feetOf(11, 8).x, y: feetOf(11, 8).y, speed: 90, wait: false },
      { op: 'camera', x: stairsTop.x, y: stairsTop.y + 40, ms: 1100 },
      { op: 'say', dialogue: 'inn-look-around' },
      { op: 'camera', follow: true, ms: 800 },
      { op: 'setMark', mark: 'inn:seated' },
    ],
  },
  {
    id: 'inn-argument',
    skippable: true,
    steps: [
      { op: 'wait', ms: 500 },
      { op: 'sfx', key: 'table-slam' },
      { op: 'camera', shake: { ms: 160, intensity: 0.004 } },
      { op: 'emote', actor: CAST.wu, emote: 'exclaim', ms: 700 },
      { op: 'say', dialogue: 'inn-arg-intro' },
      { op: 'camera', x: tableCentre.x, y: tableCentre.y, ms: 500 },
      { op: 'face', actor: CAST.wu, facing: 'left' },
      { op: 'say', dialogue: 'inn-arg-wu-1' },
      { op: 'emote', actor: CAST.luo, emote: 'flushed', ms: 900 },
      { op: 'face', actor: CAST.luo, facing: 'up' },
      { op: 'say', dialogue: 'inn-arg-luo-1' },
      { op: 'say', dialogue: 'inn-arg-wu-2' },
      { op: 'say', dialogue: 'inn-arg-luo-2' },
      { op: 'say', dialogue: 'inn-arg-wu-3a' },
      // 吴庸摇头、晃晃着站起来
      { op: 'emote', actor: CAST.wu, emote: 'shake', ms: 600 },
      { op: 'anim', actor: CAST.wu, state: 'idle' },
      { op: 'sfx', key: 'stool-scrape' },
      { op: 'move', actor: CAST.wu, x: WU_STAND.x, y: WU_STAND.y, speed: 40, wait: true },
      { op: 'face', actor: CAST.wu, facing: 'down' },
      { op: 'camera', shake: { ms: 120, intensity: 0.003 } },
      { op: 'say', dialogue: 'inn-arg-wu-3b' },
      // 众人大笑：许多人同时做表情，三位不同的酒客起哄
      { op: 'sfx', key: 'crowd-laugh' },
      { op: 'parallel', steps: LAUGHERS.map((id): TimelineStep => ({ op: 'emote', actor: id, emote: 'laugh', ms: 1600 })) },
      { op: 'say', dialogue: 'inn-arg-crowd' },
      // 吴庸转过头，看着罗大湖，努努嘴
      { op: 'face', actor: CAST.wu, facing: 'left' },
      { op: 'emote', actor: CAST.wu, emote: 'smug', ms: 800 },
      { op: 'say', dialogue: 'inn-arg-wu-4' },
      // 罗大湖眼睛暗了下来，低头喝酒
      { op: 'emote', actor: CAST.luo, emote: 'dim', ms: 1200 },
      { op: 'face', actor: CAST.luo, facing: 'down' },
      { op: 'say', dialogue: 'inn-arg-luo-dim' },
      // 罗大湖保持 dim（低头、杯在膝上）直到章末定格——不再切回 sit
      { op: 'move', actor: CAST.wu, x: SEATS.wu.x, y: SEATS.wu.y, speed: 40, wait: true },
      { op: 'face', actor: CAST.wu, facing: 'down' },
      { op: 'anim', actor: CAST.wu, state: 'sit' },
      { op: 'setFlag', flag: 'INN_ARGUMENT' },
    ],
  },
  {
    id: 'inn-omen',
    skippable: false,
    steps: [
      { op: 'wait', ms: 700 },
      { op: 'face', actor: CAST.player, facing: 'left' },
      { op: 'say', dialogue: 'inn-omen-1' },
      // 音乐换成不安主题；环境音收窄（duck）；像素化/去色脉冲；心跳
      { op: 'music', key: 'bgm-omen', fadeMs: 1200 },
      { op: 'fx', effect: 'omen-ambience', params: { duck: 0.35, ms: 2800 }, wait: false },
      { op: 'fx', effect: 'omen-glitch', params: { ms: 2800, strength: 0.6 }, wait: false },
      { op: 'sfx', key: 'heartbeat' },
      // 镜头缓推（终点为整数倍 2×，遵守整数缩放规则；镜头继续跟随坐着的江子潮）
      { op: 'parallel', steps: [
        { op: 'camera', zoom: 2, ms: 2800 },
        { op: 'emote', actor: CAST.player, emote: 'unease', ms: 1600 },
      ] },
      { op: 'say', dialogue: 'inn-omen-2' },
      // 他扭头朝赵呈博看去
      { op: 'face', actor: CAST.player, facing: 'down' },
      { op: 'wait', ms: 400 },
      { op: 'face', actor: CAST.zhao, facing: 'left' },
      { op: 'emote', actor: CAST.zhao, emote: 'frown', ms: 900 },
      { op: 'say', dialogue: 'inn-omen-3' },
      // 定格于赵呈博的注视（场景在此步开始时冻结动画并把“眉头紧缩”表情固定住）
      { op: 'fx', effect: 'freeze-frame', params: { actor: CAST.zhao, ms: 1800 }, wait: true },
      { op: 'fade', out: true, ms: 1400, color: '#05070a' },
      { op: 'setFlag', flag: 'OMEN_END' },
    ],
  },
];

export const INN_TIMELINE_IDS = INN_TIMELINES.map((t) => t.id);
